// @ts-strict-ignore
import React from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Box, PagingSidePanelType, RowsSkeletonLoader, SidePanel } from '@node-space/ui-components'
import { appBannersId } from 'components/app/AppBanners'
import { SidePanelHeaderTemplateThree } from 'components/SidePanel/SidePanelHeaderTemplates/SidePanelHeaderTemplateThree'
import { PathNames } from 'constants/General'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useGetCryptoMassPayoutById } from 'hooks/queries/MassPayouts/useGetCryptoMassPayoutById'
import { useComponentHeight } from 'hooks/useComponentHeight'
import { WalletTypes } from 'types/types'
import { highlightRowBasedOnId } from 'utils/table/table'
import { MassPayoutCryptoBatch } from '../../types'
import { MassPayoutListSidePanelBody } from '../MassPayoutListSidePanelBody'

export type MassPayoutListSummaryProps = {
  isOpen: boolean
  massPayoutDetails: MassPayoutCryptoBatch
  pageMassPayout: PagingSidePanelType
  onClose: () => void
}

export type SidePanelBodyDataProps = {
  massPayoutDetails: MassPayoutCryptoBatch
  country: string
}

export const CryptoBatchSidePanel = ({
  isOpen,
  massPayoutDetails,
  pageMassPayout,
  onClose,
}: MassPayoutListSummaryProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const bannerSpacing = useComponentHeight(appBannersId)

  const { data: cryptoBatch, isPending } = useGetCryptoMassPayoutById(
    massPayoutDetails?.externalId,
    !!massPayoutDetails?.externalId
  )

  const numberOfProcessedPayments = cryptoBatch?.totalProcessedItems
  const numberOfPayments = cryptoBatch?.totalItems
  const fileName = cryptoBatch?.fileName
  const dateCreated = dayjs(cryptoBatch?.createdAt).format('DD MMMM YYYY')
  const uploadedBy = cryptoBatch?.userName

  const paidFromWallet = `${cryptoBatch?.walletName ?? cryptoBatch?.merchantName} (${cryptoBatch?.walletCurrency})`

  const handleViewBatchTransaction = () => {
    if (massPayoutDetails?.externalId) {
      navigate(
        `${PathNames.MASS_PAYOUTS}/payout-details/${massPayoutDetails.externalId}?batchType=${WalletTypes.CRYPTO}`
      )
    }
  }

  return (
    <SidePanel
      id="side-panel-crypto-batch"
      isOpen={isOpen}
      paging={pageMassPayout}
      onClose={onClose}
      activeRowId={massPayoutDetails?.externalId}
      onActiveRowChange={highlightRowBasedOnId}
      topOffset={bannerSpacing}
      header={
        isPending ? (
          <RowsSkeletonLoader rows={1} />
        ) : (
          <SidePanelHeaderTemplateThree
            heading={fileName}
            description={t('massPayouts.numberOfPayoutsProcessed', {
              numberOfProcessedPayments: numberOfProcessedPayments,
              numberOfPayments: numberOfPayments,
            })}
          />
        )
      }
      body={
        <Box tag="section" width="full" background="standard">
          {isPending ? (
            <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
          ) : (
            <MassPayoutListSidePanelBody
              dateCreated={dateCreated}
              uploadedBy={uploadedBy}
              paidFromWallet={paidFromWallet}
              batchReference={massPayoutDetails?.externalId}
              batchStatus={cryptoBatch?.status}
              onViewBatchTransaction={handleViewBatchTransaction}
            />
          )}
        </Box>
      }
    />
  )
}
